import { fx_crearBaseLocal, reqToPromise, txDone } from "../../../../utils/idbUtils";

const DB_NAME = "grpc_rotacion_pesaje_veterinaria";
const DB_VERSION = 3;
const STORE_ROTACIONES = "rotaciones";
const STORE_SYNC = "sync";

/** Item mínimo que necesita el merge de delta (watermark por finca). */
export type GrpcRotacionPesajeVeterinariaItem = {
  id: string;
  eliminado: boolean;
  ultimaActualizacion: bigint;
  fincaId: string;
};

/** IndexedDB **nueva y separada** para los eventos de rotación/pesaje/
 *  veterinaria que trae `RotacionPesajeVeterinariaGrpcService` — mismo
 *  patrón que `GrpcCompraAnimalesDatabase`: delta real por `fincaId`. */
class GrpcRotacionPesajeVeterinariaDatabase {
  /** Conexión a la base local con el ayudante común, que al cambiar de
   *  versión conserva los datos (ver `fx_crearBaseLocal`). La versión 3
   *  (2026-09-30) se subió a pedido del usuario para una limpieza única al
   *  estrenar el ayudante: un navegador con una versión anterior vacía la
   *  base una sola vez y la vuelve a llenar desde el servidor. */
  private readonly _base = fx_crearBaseLocal(
    DB_NAME,
    DB_VERSION,
    [
      { nombre: STORE_ROTACIONES, clave: "id", indices: ["fincaId"] },
      { nombre: STORE_SYNC, clave: "fincaId" },
    ],
    { vaciarAntesDe: 3 },
  );

  /** Conexión a la base local, abierta la primera vez y reusada después. */
  private open(): Promise<IDBDatabase> {
    return this._base.abrir();
  }

  async getUltimaSincronizacionAsync(fincaId: string): Promise<bigint> {
    const db = await this.open();
    const store = db.transaction(STORE_SYNC, "readonly").objectStore(STORE_SYNC);
    const row = await reqToPromise<{ fincaId: string; ultimaSincronizacion: string } | undefined>(
      store.get(fincaId),
    );
    return row ? BigInt(row.ultimaSincronizacion) : 0n;
  }

  async putDeltaAsync<T extends GrpcRotacionPesajeVeterinariaItem>(
    fincaId: string,
    items: readonly T[],
    nuevoWatermark: bigint,
  ): Promise<void> {
    const db = await this.open();
    const tx = db.transaction([STORE_ROTACIONES, STORE_SYNC], "readwrite");
    const store = tx.objectStore(STORE_ROTACIONES);
    const syncStore = tx.objectStore(STORE_SYNC);

    for (const item of items) {
      if (item.eliminado) {
        store.delete(item.id);
      } else {
        store.put(item);
      }
    }
    syncStore.put({ fincaId, ultimaSincronizacion: nuevoWatermark.toString() });

    await txDone(tx);
  }

  async getListByFincaIdAsync<T>(fincaId: string): Promise<T[]> {
    const db = await this.open();
    const index = db
      .transaction(STORE_ROTACIONES, "readonly")
      .objectStore(STORE_ROTACIONES)
      .index("fincaId");
    return reqToPromise<T[]>(index.getAll(fincaId));
  }

  async putOneAsync<T extends GrpcRotacionPesajeVeterinariaItem>(item: T): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_ROTACIONES, "readwrite");
    tx.objectStore(STORE_ROTACIONES).put(item);
    await txDone(tx);
  }

  async deleteOneAsync(id: string): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_ROTACIONES, "readwrite");
    tx.objectStore(STORE_ROTACIONES).delete(id);
    await txDone(tx);
  }
}

/** Singleton de la base de eventos de rotación/pesaje/veterinaria por
 *  gRPC-Web (con delta por finca). */
export const grpcRotacionPesajeVeterinariaDatabase =
  new GrpcRotacionPesajeVeterinariaDatabase();
